import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { GalleryDto, GalleryTheme, Typography } from '@nusign/picflow-backend-types';
import { take } from 'rxjs';
import { debounce } from '../../../lib/debounce';
import { GalleryThemeService } from '../../../services/gallery-theme.service';
import { GalleryService } from '../../../services/gallery.service';
import { SectionService } from '../../../services/section.service';
import { TogglerComponent } from '../../../shared-extra/components/toggler/toggler.component';
import { ApplyTypographyStylesDirective } from '../../directives/apply-typography-styles.directive';
import { ThemeFontFamilyItemComponent } from '../theme-font-family-item/theme-font-family-item.component';
import { ThemeFontSizeItemComponent } from '../theme-font-size-item/theme-font-size-item.component';
import { ThemeFontStyleItemComponent } from '../theme-font-style-item/theme-font-style-item.component';
import { ThemeFontWeightItemComponent } from '../theme-font-weight-item/theme-font-weight-item.component';
import { ThemeLetterSpacingItemComponent } from '../theme-letter-spacing-item/theme-letter-spacing-item.component';
import { ThemeSingleColorItemComponent } from '../theme-single-color-item/theme-single-color-item.component';
import { ThemeTextTransformItemComponent } from '../theme-text-transform-item/theme-text-transform-item.component';

@Component({
  selector: 'app-theme-headings-item',
  imports: [
    ThemeFontFamilyItemComponent,
    ThemeSingleColorItemComponent,
    ThemeFontSizeItemComponent,
    ThemeFontWeightItemComponent,
    ThemeFontStyleItemComponent,
    ThemeTextTransformItemComponent,
    ThemeLetterSpacingItemComponent,
    TogglerComponent,
    ApplyTypographyStylesDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './theme-headings-item.component.html',
  styleUrl: './theme-headings-item.component.scss',
})
export class ThemeHeadingsItemComponent {
  private galleryService = inject(GalleryService);
  private sectionService = inject(SectionService);
  private galleryThemeService = inject(GalleryThemeService);
  gallery = input.required<GalleryDto>();
  theme = input.required<GalleryTheme>();
  open = input<boolean>();
  onOpen = output();
  onClose = output();

  updateSectionTitle(params: Partial<Typography>) {
    this.galleryThemeService
      .getExtraFontFamilyUpdateParams(params, this.theme().section.title_typography, true)
      .pipe(take(1))
      .subscribe(adjustedParams => {
        this.galleryService.optimisticUpdateGallery(this.gallery(), {
          theme: {
            ...this.theme(),
            section: {
              ...this.theme().section,
              title_typography: {
                ...this.theme().section.title_typography,
                ...adjustedParams,
              },
            },
          },
        });
        this.sectionService
          .getSections(this.gallery().id)
          .pipe(take(1))
          .subscribe(sections => {
            sections.forEach(section => {
              const sectionNeedsUpdating = Object.keys(adjustedParams).some(
                key => section.text_title_typography[key] !== null
              );
              if (!sectionNeedsUpdating) return;
              this.sectionService
                .updateSection(section, {
                  text_title_typography: {
                    font_color: adjustedParams.font_color ? null : section.text_title_typography.font_color,
                    font_family: adjustedParams.font_family ? null : section.text_title_typography.font_family,
                    font_size: adjustedParams.font_size ? null : section.text_title_typography.font_size,
                    text_alignment: adjustedParams.text_alignment ? null : section.text_title_typography.text_alignment,
                    text_transform: adjustedParams.text_transform ? null : section.text_title_typography.text_transform,
                    font_weight: adjustedParams.font_weight ? null : section.text_title_typography.font_weight,
                    font_style: adjustedParams.font_style ? null : section.text_title_typography.font_style,
                    letter_spacing: adjustedParams.letter_spacing ? null : section.text_title_typography.letter_spacing,
                  },
                })
                .subscribe();
            });
          });
      });
    this.saveSectionTitle();
  }

  private saveSectionTitle = debounce(() => {
    this.galleryService.updateGallery(this.gallery(), {
      theme: this.theme(),
    });
  }, 500);
}
